// client/src/components/pages/AmendmentsAllocateTransactions/TransactionDetailsModal.tsx
// (converted from .js, ST-00221). "View details" on the Amendments page and Transaction Search.
import { formatMiningDateTime } from '../../../utils/dateUtils';

export interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The row as the list returned it, keyed by column name. */
  transactionData: Record<string, unknown> | null;
}

// Function to format keys for display (e.g., 'AmendmentUser' -> 'Amendment User')
const formatKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

/** An instant in UTC: the app's own timestamps (AmendmentTimestamp, …) arrive as '…Z'. */
const UTC_SUFFIX = /(Z|[+-]00:?00)$/;

// Function to format values, e.g. date strings
const formatValue = (value: unknown): string => {
  if (value === null || typeof value === 'undefined') return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  // Attempt to format if it looks like a date string
  if (typeof value === 'string' && (value.includes('T') || /\d{4}-\d{2}-\d{2}/.test(value))) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      // App timestamps are UTC: printing their digits showed 12:28 for an amendment made at
      // 14:28 SAST (AMD-46). Show them in mining time.
      if (UTC_SUFFIX.test(value)) return formatMiningDateTime(value);
      // Source / weighbridge timestamps are SAST '+02:00' strings: their digits are right.
      if (value.includes('T')) {
        // Convert ISO format to database format (YYYY-MM-DD HH:MM:SS)
        return value.replace('T', ' ').substring(0, 19);
      }
      // If it's already in database format, return as-is
      return value;
    }
  }
  return String(value);
};

const TransactionDetailsModal = ({
  isOpen,
  onClose,
  transactionData,
}: TransactionDetailsModalProps) => {
  if (!isOpen || !transactionData) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center"
      onClick={onClose}
      data-testid="transaction-details-scrim"
    >
      <div
        className="relative mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mt-3 text-center">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Transaction Details</h3>
          <div className="mt-2 px-7 py-3 max-h-96 overflow-y-auto">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              {Object.entries(transactionData).map(([key, value]) => (
                <div
                  key={key}
                  className="grid grid-cols-2 items-start py-2 border-b border-gray-200"
                >
                  <dt className="font-semibold text-gray-700 text-left pr-2">{formatKey(key)}:</dt>
                  <dd className="text-gray-600 text-left break-words">{formatValue(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="items-center px-4 py-3 mt-4">
            <button
              id="close-transaction-details-modal"
              onClick={onClose}
              className="px-4 py-2 bg-primary-500 text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
